import React from 'react'
import { Loader2, Database, MessagesSquare, Mail, type LucideIcon } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useIntegrations } from '../hooks/useIntegrations'
import type { IntegrationSummary } from '../types'
import SlackIntegrationCard from '@/features/slack/components/SlackIntegrationCard'
import HubSpotIntegrationCard from '@/features/hubspot/components/HubSpotIntegrationCard'
import GoogleWorkspaceIntegrationCard from '@/features/google-workspace/components/GoogleWorkspaceIntegrationCard'
import OutlookWorkspaceIntegrationCard from '@/features/outlook-workspace/components/OutlookWorkspaceIntegrationCard'
import { combineGoogleWorkspaceStatus } from '@/features/google-workspace/lib/status'
import { combineOutlookWorkspaceStatus } from '@/features/outlook-workspace/lib/status'
import MainCalendarPreference from '@/features/bookings/components/MainCalendarPreference'

type Props = { workspaceId: string }

type Category = {
  label: string
  description: string
  Icon: LucideIcon
  /** Summaries for the providers in this category, used only for the connected count */
  summaries: (IntegrationSummary | null | undefined)[]
  cards: React.ReactNode
}

const IntegrationsSection: React.FC<Props> = ({ workspaceId }) => {
  const { currentTheme: t } = useTheme()
  const { integrations, loading, refetch } = useIntegrations(workspaceId)

  const slack = integrations.find((i) => i.provider === 'slack') ?? null
  const gmail = integrations.find((i) => i.provider === 'gmail')
  const outlook = integrations.find((i) => i.provider === 'outlook')
  const hubspot = integrations.find((i) => i.provider === 'hubspot')
  const googleCalendar = integrations.find((i) => i.provider === 'google_calendar')
  const outlookCalendar = integrations.find((i) => i.provider === 'outlook_calendar')

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} />
      </div>
    )
  }

  const categories: Category[] = [
    {
      label: 'CRM',
      description: 'Read and update lead records so Zeph works from live pipeline data.',
      Icon: Database,
      summaries: [hubspot],
      cards: (
        <HubSpotIntegrationCard workspaceId={workspaceId} summary={hubspot} onRefresh={refetch} />
      ),
    },
    {
      label: 'Communication',
      description: 'Assign tasks to Zeph and get updates where your team already works.',
      Icon: MessagesSquare,
      summaries: [slack],
      cards: (
        <SlackIntegrationCard workspaceId={workspaceId} summary={slack} onRefresh={refetch} />
      ),
    },
    {
      label: 'Email & Calendar',
      description: 'Let Zeph read, send, and follow up on prospect email, and see your schedule.',
      Icon: Mail,
      // Synthetic summaries for the count badge — Google Workspace and Outlook
      // Workspace are each one combined connection (Mail + Calendar), not two
      // separate ones.
      summaries: [
        { provider: 'gmail', configured: true, connected: false, status: combineGoogleWorkspaceStatus(gmail, googleCalendar) },
        { provider: 'outlook', configured: true, connected: false, status: combineOutlookWorkspaceStatus(outlook, outlookCalendar) },
      ],
      cards: (
        <>
          <GoogleWorkspaceIntegrationCard workspaceId={workspaceId} gmailSummary={gmail} calendarSummary={googleCalendar} onRefresh={refetch} />
          <OutlookWorkspaceIntegrationCard workspaceId={workspaceId} outlookSummary={outlook} outlookCalendarSummary={outlookCalendar} onRefresh={refetch} />
          <MainCalendarPreference workspaceId={workspaceId} />
        </>
      ),
    },
  ]

  return (
    <div className="space-y-9">
      {categories.map((category, i) => {
        const connectedCount = category.summaries.filter((s) => s?.status === 'active').length
        return (
          <section
            key={category.label}
            className={i > 0 ? 'pt-9' : undefined}
            style={i > 0 ? { borderTop: `1px solid ${t.borderSecondary}` } : undefined}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: t.hoverBackground, border: `1px solid ${t.border}` }}
              >
                <category.Icon size={15} style={{ color: t.secondrytext }} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold" style={{ color: t.text }}>
                    {category.label}
                  </h3>
                  {connectedCount > 0 && (
                    <span
                      className="text-[11px] font-medium px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: t.successBackground, color: t.successText }}
                    >
                      {connectedCount} connected
                    </span>
                  )}
                </div>
                <p className="text-xs mt-0.5" style={{ color: t.mutedText }}>
                  {category.description}
                </p>
              </div>
            </div>
            <div className="space-y-3">{category.cards}</div>
          </section>
        )
      })}
    </div>
  )
}

export default React.memo(IntegrationsSection)
